import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Divider,
  Stack,
} from '@mui/material';
import {
  Description,
  CloudUpload,
  Delete,
  Download,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { Client } from '@/types';
import { format } from 'date-fns';

// Mock function for now, in a real app this would upload to Firebase Storage
const mockUpload = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(`https://storage.googleapis.com/mock-bucket/${file.name}`);
    }, 1500);
  });
};

interface ClientDocument {
  id: string;
  name: string;
  type: string;
  url: string;
  uploadedAt: Date;
  status: 'verified' | 'pending' | 'rejected';
}

interface ClientDocumentsProps {
  client: Client;
  onDocumentsUpdate?: () => void;
}

const REQUIRED_DOCS = [
  { id: 'cnie', label: 'CNIE (National ID)', description: 'Copy of Moroccan ID or Passport' },
  { id: 'payslips', label: '3 Months Payslips', description: 'Recent salary slips for bank instruction' },
  { id: 'bank_statements', label: 'Bank Statements', description: 'Last 3 months of account activity' },
  { id: 'accord_principe', label: 'Accord de Principe', description: 'Bank pre-approval document' },
];

const ClientDocuments: React.FC<ClientDocumentsProps> = ({ client }) => {
  // In a real app, this would be part of the client object or a subcollection
  const [documents, setDocuments] = useState<ClientDocument[]>([
    {
      id: 'doc1',
      name: 'cnie_front_back.pdf',
      type: 'cnie',
      url: '#',
      uploadedAt: new Date(Date.now() - 86400000 * 2),
      status: 'verified',
    }
  ]);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadType, setUploadType] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleOpenUpload = (typeId: string) => {
    setUploadType(typeId);
    setSelectedFile(null);
    setUploadOpen(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    
    try {
      const url = await mockUpload(selectedFile);
      const newDoc: ClientDocument = {
        id: `doc_${Date.now()}`,
        name: selectedFile.name,
        type: uploadType,
        url,
        uploadedAt: new Date(),
        status: 'pending',
      };
      
      setDocuments([...documents, newDoc]);
      setUploadOpen(false);
    } catch (error) {
      console.error("Upload failed", error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (id: string) => {
    setDocuments(documents.filter(d => d.id !== id));
  };

  const getDocStatusIcon = (status: string) => {
    switch (status) {
      case 'verified': return <CheckCircle color="success" />;
      case 'rejected': return <Warning color="error" />;
      default: return <Warning color="warning" />;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Legal & Financial Documents</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage required documentation for the Moroccan transaction pipeline.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<CloudUpload />} onClick={() => handleOpenUpload('other')}>
          Upload Custom File
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined">
            <CardContent sx={{ p: 0 }}>
              <List disablePadding>
                {documents.map((doc, index) => (
                  <React.Fragment key={doc.id}>
                    <ListItem 
                      sx={{ py: 2, px: 3 }}
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton size="small" title="Download">
                            <Download fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(doc.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemIcon>
                        <Description color="primary" fontSize="large" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={doc.name}
                        secondary={`Uploaded ${format(doc.uploadedAt, 'MMM dd, yyyy')} • Type: ${REQUIRED_DOCS.find(r => r.id === doc.type)?.label || 'Other'}`}
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                      <Box sx={{ mr: 6 }}>
                        <Chip 
                          icon={getDocStatusIcon(doc.status)} 
                          label={doc.status.toUpperCase()} 
                          size="small"
                          color={doc.status === 'verified' ? 'success' : doc.status === 'rejected' ? 'error' : 'warning'}
                          variant="outlined"
                        />
                      </Box>
                    </ListItem>
                    {index < documents.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
                
                {documents.length === 0 && (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Description sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.2, mb: 2 }} />
                    <Typography color="text.secondary">No documents uploaded yet.</Typography>
                  </Box>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'rgba(197, 160, 89, 0.03)', border: '1px solid rgba(197, 160, 89, 0.2)' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                Required Dossier
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                To progress to "Bank Instruction" or "Compromis", these documents are strictly required.
              </Typography>
              
              <Stack spacing={2}>
                {REQUIRED_DOCS.map(req => {
                  const isUploaded = documents.some(d => d.type === req.id);
                  return (
                    <Box key={req.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: isUploaded ? 'success.light' : 'divider' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: isUploaded ? 'success.main' : 'text.primary' }}>
                          {req.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{req.description}</Typography>
                      </Box>
                      {isUploaded ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <Button size="small" variant="outlined" onClick={() => handleOpenUpload(req.id)}>Upload</Button>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onClose={() => !uploading && setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ textAlign: 'center', py: 4, border: '2px dashed', borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50' }}>
            <input
              type="file"
              id="file-upload"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png"
            />
            <label htmlFor="file-upload">
              <Button component="span" variant="outlined" startIcon={<CloudUpload />}>
                Select File
              </Button>
            </label>
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 2, fontWeight: 600 }}>
                Selected: {selectedFile.name}
              </Typography>
            )}
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
              Supported formats: PDF, JPG, PNG (Max 5MB)
            </Typography>
          </Box>
          {uploading && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleUpload} variant="contained" disabled={!selectedFile || uploading}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientDocuments;
